// The parser for the `html` element.
//
// It returns a TREE, not a string. That is the whole difference from the version this
// replaced: a card can carry native controls, and a control cannot be placed inside a
// table cell unless the client builds the cell itself. Building the tree here means the
// app never inserts delivered markup into a document as markup — there is no sandboxed
// frame to escape from and no raw-HTML injection to get wrong, because neither exists.
//
// What the subset allows: a fixed tag list, `colspan`/`rowspan` as integers on table
// cells, and `<ctl i="N">` — an empty numbered slot whose meaning comes from the
// element's separate `controls` array. The markup can say WHERE a control goes. It has
// no way to say what one does.
//
// The server enforces the same rules on the way in. This is the second half of not
// trusting the other side, not a duplicate of it.

import { defang } from './link-guard';

export const ALLOWED_TAGS = [
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  'span',
  'div',
] as const;

export type AllowedTag = (typeof ALLOWED_TAGS)[number];

const ALLOWED = new Set<string>(ALLOWED_TAGS);

/** Elements whose CONTENT must vanish with them, not be unwrapped. */
const DROP_ENTIRELY = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'LINK',
  'META',
  'TEMPLATE',
  'NOSCRIPT',
  'FORM',
  'INPUT',
  'BUTTON',
  'SELECT',
  'TEXTAREA',
  'SVG',
  'MATH',
]);

/** How deep the tree may go. Beyond this the card is not a card any more, and a renderer
 *  that recurses without a floor is a renderer someone can stall with one payload. */
const MAX_DEPTH = 20;

/** How many nodes may survive. A 500-row table is not a spec sheet; it is a way to make
 *  a phone busy. */
const MAX_NODES = 2000;

export type CardNode =
  | { kind: 'text'; text: string }
  | { kind: 'slot'; index: number }
  | {
      kind: 'element';
      tag: AllowedTag;
      colspan: number | undefined;
      rowspan: number | undefined;
      children: CardNode[];
    };

function integerAttribute(element: Element, name: string): number | undefined {
  const value = element.getAttribute(name);
  if (value === null || !/^[0-9]{1,3}$/.test(value)) {
    return undefined;
  }
  const parsed = Number(value);
  return parsed > 0 ? parsed : undefined;
}

function walk(from: Node, depth: number, budget: { left: number }): CardNode[] {
  if (depth > MAX_DEPTH || budget.left <= 0) {
    return [];
  }
  const out: CardNode[] = [];
  for (const child of Array.from(from.childNodes)) {
    if (budget.left <= 0) {
      break;
    }
    if (child.nodeType === Node.TEXT_NODE) {
      const text = defang(child.textContent ?? '');
      if (text.length > 0) {
        budget.left--;
        out.push({ kind: 'text', text });
      }
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue; // comments, processing instructions — gone
    }
    const element = child as Element;
    const tag = element.tagName.toUpperCase();

    if (DROP_ENTIRELY.has(tag)) {
      continue;
    }

    if (tag === 'CTL') {
      // A slot with no readable index cannot be matched to a control, so it is not a
      // slot and nothing is drawn. Its children are dropped too: a slot is empty by
      // definition, and content inside one would be content the author expected us to
      // show around a button we place ourselves.
      const index = integerAttribute(element, 'i');
      const zero = element.getAttribute('i') === '0';
      if (index !== undefined || zero) {
        budget.left--;
        out.push({ kind: 'slot', index: index ?? 0 });
      }
      continue;
    }

    if (!ALLOWED.has(tag.toLowerCase())) {
      // Unknown formatting is unwrapped: the content survives, the tag does not.
      out.push(...walk(element, depth + 1, budget));
      continue;
    }

    const lower = tag.toLowerCase() as AllowedTag;
    budget.left--;
    out.push({
      kind: 'element',
      tag: lower,
      colspan: lower === 'td' || lower === 'th' ? integerAttribute(element, 'colspan') : undefined,
      rowspan: lower === 'td' || lower === 'th' ? integerAttribute(element, 'rowspan') : undefined,
      children: walk(element, depth + 1, budget),
    });
  }
  return out;
}

/** Parses a card into the tree the renderer draws. An empty array means nothing
 *  survived, and the caller shows `fallback_text` instead. */
export function parseHtmlCard(html: string): CardNode[] {
  if (html.length > 20000) {
    return [];
  }
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const nodes = walk(parsed.body, 0, { left: MAX_NODES });
  return hasSubstance(nodes) ? nodes : [];
}

/** Whitespace-only markup renders as nothing, so it counts as nothing. A rule, an image
 *  or a slot is substance even without a word beside it. */
function hasSubstance(nodes: CardNode[]): boolean {
  for (const node of nodes) {
    if (node.kind === 'text' && node.text.trim().length > 0) {
      return true;
    }
    if (node.kind === 'slot') {
      return true;
    }
    if (node.kind === 'element') {
      if (node.tag === 'hr' || node.tag === 'br') {
        return true;
      }
      if (hasSubstance(node.children)) {
        return true;
      }
    }
  }
  return false;
}
